import { useState, useEffect } from 'react'
import { useAuth } from '../providers/AuthProvider'
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  User, 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ArrowLeft,
  Loader2
} from 'lucide-react'

interface LoginProps {
  onSuccess?: () => void
  onBack?: () => void
}

type AuthMode = 'signin' | 'signup' | 'forgot-password' | 'reset-password'

interface PasswordStrength {
  score: number
  feedback: string[]
  isValid: boolean
}

const checkPasswordStrength = (password: string): PasswordStrength => {
  const feedback: string[] = []
  let score = 0

  if (password.length >= 8) {
    score += 1
  } else {
    feedback.push('At least 8 characters')
  }

  if (/[A-Z]/.test(password)) {
    score += 1
  } else {
    feedback.push('One uppercase letter')
  }

  if (/[a-z]/.test(password)) {
    score += 1
  } else {
    feedback.push('One lowercase letter')
  }

  if (/\d/.test(password)) {
    score += 1
  } else {
    feedback.push('One number')
  }

  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    score += 1
  } else {
    feedback.push('One special character')
  }

  return {
    score,
    feedback,
    isValid: score >= 4
  }
}

export default function Login({ onSuccess, onBack }: LoginProps) {
  const { 
    signIn, 
    signUp, 
    resetPassword, 
    checkEmailVerified,
    resendVerification,
    error: authError,
    isConfigured,
    clearError,
    retryConnection
  } = useAuth()
  
  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({ 
    score: 0, 
    feedback: [], 
    isValid: false 
  })
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  useEffect(() => {
    if (mode === 'signup' && password) {
      setPasswordStrength(checkPasswordStrength(password))
    }
  }, [password, mode])

  const validateForm = (): boolean => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      return false
    }

    if (mode === 'forgot-password') return true

    if (!password) {
      setError('Password is required')
      return false
    }

    if (mode === 'signup') {
      if (!firstName.trim() || !lastName.trim()) {
        setError('First and last name are required')
        return false
      }

      if (!passwordStrength.isValid) {
        setError('Password does not meet security requirements')
        return false
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match')
        return false
      }

      if (!agreedToTerms) {
        setError('You must agree to the Terms of Service and Privacy Policy')
        return false
      }
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    clearError() // Clear any auth provider errors

    if (!validateForm()) return

    // Check if Supabase is configured
    if (!isConfigured) {
      setError('Authentication not configured. Please check your environment variables.')
      return
    }

    setLoading(true)

    try {
      switch (mode) {
        case 'signin':
          await signIn(email, password)
          // Clear any stored signup names once account row is created on login
          sessionStorage.removeItem('signupFirstName')
          sessionStorage.removeItem('signupLastName')
          setSuccess('Successfully signed in!')
          onSuccess?.()
          break

        case 'signup':
          await signUp(email, password, firstName, lastName)
          // Clear any previously stored names since account creation was successful
          sessionStorage.removeItem('signupFirstName')
          sessionStorage.removeItem('signupLastName')
          setSuccess('Account created! Please check your email to verify your account before signing in.')
          setTimeout(() => setMode('signin'), 2000)
          break

        case 'forgot-password':
          await resetPassword(email)
          setSuccess('Password reset link sent! Check your email for instructions.')
          setTimeout(() => setMode('signin'), 3000)
          break
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      
      // Handle common Supabase errors with user-friendly messages
      if (errorMessage.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.')
      } else if (errorMessage.includes('User already registered')) {
        setError('An account with this email already exists. Try signing in instead.')
      } else if (errorMessage.includes('Password should be at least 6 characters')) {
        setError('Password must be at least 6 characters long.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    setLoading(true)
    try {
      await resendVerification()
      setSuccess('Verification email sent! Check your inbox.')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const getPasswordStrengthColor = (score: number) => {
    if (score < 2) return 'bg-red-500'
    if (score < 4) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getPasswordStrengthText = (score: number) => {
    if (score < 2) return 'Weak'
    if (score < 4) return 'Medium'
    return 'Strong'
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center">
              {mode === 'forgot-password' ? (
                <Mail className="h-6 w-6 text-white" />
              ) : (
                <Shield className="h-6 w-6 text-white" />
              )}
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {mode === 'signin' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'forgot-password' && 'Reset Password'}
          </h2>
          <p className="text-slate-600">
            {mode === 'signin' && 'Sign in to your ZippyHealth account'}
            {mode === 'signup' && 'Join ZippyHealth to start your health journey'}
            {mode === 'forgot-password' && 'Enter your email to receive a reset link'}
          </p>
        </div>

        {/* Success/Error Messages */}
        {(error || authError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error || authError}</p>
            </div>
            {!isConfigured && (
              <div className="mt-3 space-y-2">
                <p className="text-red-600 text-xs font-medium">Environment Setup Required:</p>
                <ol className="text-red-600 text-xs list-decimal list-inside space-y-1">
                  <li>Create a <code className="bg-red-100 px-1 rounded">.env</code> file in <code className="bg-red-100 px-1 rounded">apps/main-app/</code></li>
                  <li>Add your Supabase credentials:
                    <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-x-auto">
{`VITE_SUPABASE_URL=https://your-project.supabase.co
VITE_SUPABASE_ANON_KEY=your-anon-key`}
                    </pre>
                  </li>
                  <li>Restart your development server</li>
                </ol>
                <button
                  onClick={retryConnection}
                  className="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                >
                  Retry Connection
                </button>
              </div>
            )}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Fields (Signup only) */}
          {mode === 'signup' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          )}

          {/* Email Field */}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
            />
          </div>

          {/* Password Fields */}
          {mode !== 'forgot-password' && (
            <>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Password Strength Indicator (Signup only) */}
              {mode === 'signup' && password && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Password strength:</span>
                    <span className={`text-sm font-medium ${
                      passwordStrength.score < 2 ? 'text-red-600' :
                      passwordStrength.score < 4 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {getPasswordStrengthText(passwordStrength.score)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength.score)}`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="text-xs text-slate-600">
                      Missing: {passwordStrength.feedback.join(', ')}
                    </div>
                  )}
                </div>
              )}

              {/* Confirm Password (Signup only) */}
              {mode === 'signup' && (
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Remember Me (Signin only) */}
          {mode === 'signin' && (
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                />
                <span className="ml-2 text-sm text-slate-600">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setMode('forgot-password')}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Terms Agreement (Signup only) */}
          {mode === 'signup' && (
            <label className="flex items-start space-x-2">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 rounded border-slate-300 text-red-600 focus:ring-red-500"
                required
              />
              <span className="text-sm text-slate-600">
                I agree to the{' '}
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-700"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-700"
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          )}

          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
          >
            {loading && <Loader2 className="h-5 w-5 animate-spin" />}
            <span>
              {mode === 'signin' && (loading ? 'Signing in...' : 'Sign In')}
              {mode === 'signup' && (loading ? 'Creating account...' : 'Create Account')}
              {mode === 'forgot-password' && (loading ? 'Sending...' : 'Send Reset Link')}
            </span>
          </button>
        </form>

        {/* Mode Switching */}
        <div className="mt-6 text-center space-y-2">
          {mode === 'signin' && (
            <>
              <p className="text-sm text-slate-600">
                Don't have an account?{' '}
                <button 
                  type="button" 
                  className="text-red-600 hover:text-red-700 font-medium"
                  onClick={() => setMode('signup')}
                >
                  Sign up
                </button>
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Resend verification email
              </button>
            </>
          )}

          {mode === 'signup' && (
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <button 
                type="button" 
                className="text-red-600 hover:text-red-700 font-medium"
                onClick={() => setMode('signin')}
              >
                Sign in
              </button>
            </p>
          )}

          {mode === 'forgot-password' && (
            <button 
              type="button" 
              className="text-sm text-slate-600 hover:text-slate-800 flex items-center justify-center space-x-1"
              onClick={() => setMode('signin')}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to sign in</span>
            </button>
          )}
        </div>

        {/* Back Button */}
        {onBack && (
          <div className="mt-6 text-center">
            <button 
              type="button" 
              onClick={onBack} 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center space-x-1"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to home</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
